// Native push-notification plumbing (iOS via @capacitor-firebase/messaging).
//
// Token lifecycle: getToken → registerPushToken (engine stores it on the user doc).
// We never prompt for permission on cold launch — `requestPushPermission` is called
// contextually (after the first pick submit, or from Settings). On sign-out we drop
// the device's token from the user and invalidate it so it stops receiving pushes.

import { Capacitor, type PermissionState } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { registerPushToken, unregisterPushToken } from './api';

/** Last token we registered this session, so sign-out can unregister precisely. */
let currentToken: string | null = null;

/** Push is only wired on native platforms (iOS) — the web build is a no-op. */
export function isPushSupported(): boolean {
  return Capacitor.isNativePlatform();
}

/** The OS-level push permission for this device (without prompting). */
export async function currentPushPermission(): Promise<PermissionState> {
  if (!isPushSupported()) return 'denied';
  try {
    const { receive } = await FirebaseMessaging.checkPermissions();
    return receive;
  } catch {
    return 'denied';
  }
}

/** Fetch the FCM token and register it for the signed-in user. Assumes permission
 *  is already granted; safe to call repeatedly (the server upserts/de-dupes). */
export async function syncPushToken(): Promise<string | null> {
  if (!isPushSupported()) return null;
  try {
    const { token } = await FirebaseMessaging.getToken();
    if (!token) return null;
    currentToken = token;
    await registerPushToken(token, Capacitor.getPlatform());
    return token;
  } catch (err) {
    console.warn('[push] token sync failed', err);
    return null;
  }
}

/**
 * Contextual permission ask — only prompts when the OS hasn't been asked yet, so
 * it's safe to fire after a natural moment (the first pick submit) without ever
 * re-nagging a user who already decided. No-op off-device.
 */
export async function maybePromptPushPermission(): Promise<void> {
  if (!isPushSupported()) return;
  const perm = await currentPushPermission();
  if (perm === 'prompt' || perm === 'prompt-with-rationale') await requestPushPermission();
}

/** Prompt for OS permission, then register the token if granted. Returns the result. */
export async function requestPushPermission(): Promise<PermissionState> {
  if (!isPushSupported()) return 'denied';
  try {
    const { receive } = await FirebaseMessaging.requestPermissions();
    if (receive === 'granted') await syncPushToken();
    return receive;
  } catch (err) {
    console.warn('[push] permission request failed', err);
    return 'denied';
  }
}

/** On sign-out: unregister this device's token from the user and invalidate it. */
export async function clearPushToken(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    let token = currentToken;
    if (!token) {
      token = await FirebaseMessaging.getToken()
        .then((r) => r.token)
        .catch(() => null);
    }
    if (token) await unregisterPushToken(token).catch(() => undefined);
    await FirebaseMessaging.deleteToken().catch(() => undefined);
  } catch (err) {
    console.warn('[push] clear failed', err);
  } finally {
    currentToken = null;
  }
}
